import { createOperationId } from '../../domain/operationId';
import { FirebaseError } from 'firebase/app';
import {
  firestorePaths,
  InventoryAuthority,
  requireOperationId,
} from '@qkindex/contracts';
import { validateItemImage } from '../../services/imageProcessing';
import { rapidCaptureDb } from './rapidCaptureDb';
import {
  RapidCaptureRecord,
  sanitizeRapidCaptureTerminalRecord,
  hasCaptureEdits,
  CaptureDraftFields,
  CaptureEditSnapshot,
} from './rapidCaptureTypes';
import {
  captureService,
  captureImageHash,
  CaptureStatus,
} from '../../services/captureService';
import { invalidateInventory } from '../collection/inventoryInvalidation';
import { recordRapidCaptureMetric } from './rapidCaptureMetrics';

const errorDetails = (
  error: unknown,
): NonNullable<RapidCaptureRecord['lastError']> => {
  const code =
    error instanceof FirebaseError
      ? error.code
      : typeof (error as { code?: unknown })?.code === 'string'
        ? (error as { code: string }).code
        : '';
  const message =
    error instanceof Error ? error.message : 'The queued operation failed.';
  const details = (
    error as { details?: { code?: unknown; retryAfterSeconds?: unknown } }
  )?.details;
  const retryAfterSeconds =
    typeof details?.retryAfterSeconds === 'number'
      ? details.retryAfterSeconds
      : undefined;
  if (details?.code === 'collection-unavailable')
    return {
      category: 'capacity',
      message:
        'This collection cannot accept uploads right now. Check the owner’s billing balance and collection availability, then retry. Your capture is kept on this device.',
    };
  if (details?.code === 'capacity-exceeded') {
    return {
      category: 'capacity',
      message:
        'This collection has reached its operational item limit. The captured draft was kept.',
    };
  }
  if (
    ['role-required', 'membership-required', 'collection-not-found'].includes(
      String(details?.code),
    )
  )
    return { category: 'permission', message };
  if (
    code === 'validation' ||
    code === 'conflict' ||
    code.includes('invalid-argument') ||
    [
      'confirmation-required',
      'operation-conflict',
      'invalid-input',
      'insufficient-credits',
      'provider-reconciliation',
    ].includes(String(details?.code))
  )
    return { category: 'conflict', message };
  if (code.includes('unauthenticated'))
    return { category: 'authentication', message };
  if (code.includes('permission')) return { category: 'permission', message };
  if (code.includes('resource-exhausted') || code.includes('quota'))
    return { category: 'quota', message, retryAfterSeconds };
  if (code.includes('network') || !navigator.onLine)
    return { category: 'network', message };
  if (message.toLocaleLowerCase().includes('already in use'))
    return { category: 'conflict', message };
  return { category: 'unknown', message };
};

const statusFailureCategory = (
  code?: string,
): NonNullable<RapidCaptureRecord['lastError']>['category'] => {
  if (
    [
      'capacity-exceeded',
      'collection-unavailable',
      'insufficient-credits',
    ].includes(code ?? '')
  )
    return 'capacity';
  if (
    [
      'role-required',
      'membership-required',
      'permission-denied',
      'collection-not-found',
    ].includes(code ?? '')
  )
    return 'permission';
  if (
    [
      'authentication-required',
      'unauthenticated',
      'verification-required',
    ].includes(code ?? '')
  )
    return 'authentication';
  if (
    ['quota-exceeded', 'resource-exhausted', 'rate-limited'].includes(
      code ?? '',
    )
  )
    return 'quota';
  if (
    [
      'confirmation-required',
      'operation-conflict',
      'item-replaced',
      'item-deleted',
      'reconciliation-required',
      'provider-reconciliation',
      'dispatch-unknown',
    ].includes(code ?? '')
  )
    return 'conflict';
  return 'unknown';
};

const uploadMissing = (error: unknown): boolean =>
  (error as { details?: { uploadMissing?: boolean } })?.details
    ?.uploadMissing === true;

export const editMutation = (
  record: RapidCaptureRecord,
  snapshot: CaptureEditSnapshot,
): RapidCaptureRecord => ({
  ...record,
  ...snapshot,
  operationId: snapshot.operationId,
  mutation: {
    expectedRevision: snapshot.expectedRevision,
    imageAction:
      snapshot.image === null ? 'remove' : snapshot.image ? 'replace' : 'keep',
    mergeKeywords: snapshot.mergeKeywords,
  },
  mutationFields: snapshot.fields,
  image: snapshot.image ?? new Blob([]),
  imageHash: snapshot.imageHash,
  uploaded: snapshot.uploaded,
  uploadAttempted: snapshot.uploadAttempted,
  itemCreated: false,
  analyzeImage: false,
});

/** One transfer protocol serves initial captures, field patches and photo replacements. */
async function transferMutation(
  initial: RapidCaptureRecord,
  persist: (
    changes: Partial<RapidCaptureRecord>,
  ) => Promise<RapidCaptureRecord | undefined>,
  active: () => boolean,
): Promise<CaptureStatus | undefined> {
  let record = initial;
  const update = async (changes: Partial<RapidCaptureRecord>) => {
    const next = await persist(changes);
    if (next) record = { ...record, ...next };
    return next && active();
  };
  const needsImage =
    !record.mutation || record.mutation.imageAction === 'replace';
  if (needsImage && !record.imageHash) {
    await validateItemImage(record.image);
    const imageHash = await captureImageHash(record.image);
    if (!active() || !(await update({ imageHash }))) return;
  }
  const begun = await captureService.begin(record);
  if (!active()) return;
  if (begun.itemCreated || begun.state === 'discarded') return begun;
  if (!(await update({ serverAccepted: true }))) return;
  if (!needsImage) return captureService.commit(record);
  if (!begun.uploadPath)
    throw new Error('The server did not grant this photo upload.');
  if (!(await update({ uploadPath: begun.uploadPath, serverAccepted: true })))
    return;
  let missing = false;
  if (record.uploaded || record.uploadAttempted) {
    try {
      return await captureService.commit(record);
    } catch (error) {
      if (!uploadMissing(error)) throw error;
      missing = true;
      if (!(await update({ uploaded: false }))) return;
    }
  }
  if (!record.uploaded || missing) {
    if (!(await update({ uploadAttempted: true }))) return;
    try {
      const started = performance.now();
      await captureService.upload(record, begun.uploadPath);
      recordRapidCaptureMetric('upload', performance.now() - started, {
        bytes: record.image.size,
      });
    } catch (uploadError) {
      try {
        return await captureService.commit(record);
      } catch (commitError) {
        throw uploadMissing(commitError) ? uploadError : commitError;
      }
    }
    if (!active() || !(await update({ uploaded: true }))) return;
  }
  const started = performance.now();
  const receipt = await captureService.commit(record);
  recordRapidCaptureMetric('item-create', performance.now() - started);
  return receipt;
}

export const rapidCaptureAuthority = (
  record: RapidCaptureRecord,
): InventoryAuthority & { membershipVersion: number } => {
  if (
    record.schemaVersion !== 6 ||
    !record.userId ||
    !record.collectionId ||
    !Number.isSafeInteger(record.membershipVersion) ||
    record.membershipVersion < 0 ||
    record.compatibilityMode !== 'collection-only' ||
    !record.itemId ||
    !(record.image instanceof Blob) ||
    !Number.isFinite(record.qty) ||
    record.qty < (record.mutation ? 0 : 1)
  )
    throw new Error('Queued collection authority is invalid.');
  requireOperationId(record.operationId);
  if (record.analysisOperationId)
    requireOperationId(record.analysisOperationId);
  firestorePaths.member(record.collectionId, record.userId);
  firestorePaths.item(record.collectionId, record.itemId);
  return {
    userId: record.userId,
    collectionId: record.collectionId,
    compatibilityMode: record.compatibilityMode,
    membershipVersion: record.membershipVersion,
  };
};

export const applyCaptureStatus = async (
  record: RapidCaptureRecord,
  result: CaptureStatus,
  acknowledgedControlAction?: RapidCaptureRecord['controlAction'],
) => {
  if (result.itemId !== undefined && result.itemId !== record.itemId)
    throw new Error('The saved item receipt does not match this capture.');
  let changed = false;
  await rapidCaptureDb.update(record, (current) => {
    if (current.itemUnavailable) return current;
    // An HTTP response acknowledges only the intent it actually sent. A newer
    // durable Save/Dismiss action must survive an older Analyze/Retry response.
    const acknowledgesCurrentControl =
      acknowledgedControlAction !== undefined &&
      current.controlAction === acknowledgedControlAction;
    const pendingControl =
      Boolean(current.controlAction) && !acknowledgesCurrentControl;
    if (
      current.serverRevision !== undefined &&
      (result.revision === undefined ||
        result.revision < current.serverRevision ||
        (result.revision === current.serverRevision &&
          !acknowledgesCurrentControl))
    )
      return current;
    if (result.state === 'discarded')
      return current.itemCreated || pendingControl ? current : undefined;
    changed = true;
    const itemCreated = result.itemCreated === true || current.itemCreated;
    const completed = result.state === 'complete';
    const waitingForAI =
      itemCreated &&
      completed &&
      current.analyzeImage &&
      !result.analysisComplete &&
      !current.aiDismissed &&
      !(acknowledgesCurrentControl && acknowledgedControlAction === 'save');
    const deferredConsent =
      itemCreated &&
      completed &&
      current.aiSkipReason === 'cost-unconfirmed' &&
      !(acknowledgesCurrentControl && acknowledgedControlAction === 'save');
    return {
      ...current,
      serverAccepted: true,
      itemCreated,
      ...(result.savedFields
        ? {
            publishedFields: result.savedFields,
            publishedRevision: result.itemRevision,
            publishedStoragePath: result.storagePath,
          }
        : {}),
      aiStarted:
        current.aiStarted ||
        acknowledgedControlAction === 'analyze' ||
        result.mode === 'analysis',
      serverRevision: result.revision ?? current.serverRevision,
      itemRevision: Math.max(
        current.itemRevision ?? 0,
        result.itemRevision ?? 0,
      ),
      controlAction: acknowledgesCurrentControl
        ? undefined
        : current.controlAction,
      state: pendingControl
        ? current.state
        : result.state === 'failed' || deferredConsent
          ? 'failed'
          : completed && !waitingForAI
            ? 'confirming'
            : itemCreated
              ? 'analysis-submitting'
              : current.state,
      completion: pendingControl
        ? current.completion
        : completed
          ? result.analysisComplete
            ? 'analysis-complete'
            : 'item'
          : result.mode === 'analysis'
            ? undefined
            : current.completion,
      aiSettlement: result.aiSettlement,
      ...(acknowledgesCurrentControl && acknowledgedControlAction === 'save'
        ? { analyzeImage: false, aiDismissed: true, aiSkipReason: undefined }
        : {}),
      lastError: pendingControl
        ? current.lastError
        : deferredConsent
          ? {
              phase: 'ai',
              category: 'conflict',
              message:
                'The item was saved. AI cost could not be confirmed; retry keyword generation or dismiss this notice.',
            }
          : result.state === 'failed'
            ? {
                phase: itemCreated ? 'ai' : 'upload',
                category: statusFailureCategory(result.errorCode),
                message:
                  result.errorMessage ||
                  (itemCreated
                    ? 'Keyword generation needs attention.'
                    : 'The item could not be saved.'),
              }
            : current.lastError?.phase === 'edits'
              ? current.lastError
              : undefined,
      aiSkipReason: pendingControl
        ? current.aiSkipReason
        : result.errorCode === 'confirmation-required'
          ? 'cost-changed'
          : acknowledgesCurrentControl && acknowledgedControlAction === 'save'
            ? undefined
            : current.aiSkipReason,
      nextAttemptAt: pendingControl ? current.nextAttemptAt : undefined,
    };
  });
  if (changed && result.itemCreated)
    invalidateInventory({
      userId: record.userId,
      collectionId: record.collectionId,
      resources: ['items'],
      reloadItemIds: [record.itemId],
    });
};

export const processRapidCaptureRecord = async (
  initial: RapidCaptureRecord,
  shouldContinue: () => boolean = () => true,
  authenticatedUserId: string = initial.userId,
  prepareConsent?: (record: RapidCaptureRecord) => Promise<string | undefined>,
): Promise<void> => {
  if (
    initial.state === 'terminal' ||
    initial.itemUnavailable ||
    initial.userId !== authenticatedUserId
  )
    return;
  let record = initial;
  let phase: 'upload' | 'ai' | 'edits' = record.controlAction
    ? 'ai'
    : record.itemCreated
      ? 'edits'
      : 'upload';
  const update = async (changes: Partial<RapidCaptureRecord>) => {
    const current = await rapidCaptureDb.update(record, (saved) => ({
      ...saved,
      ...changes,
    }));
    if (current) record = current;
    return current;
  };
  try {
    if (record.schemaVersion !== 6) {
      await update({
        state: 'failed',
        lastError: {
          phase: 'upload',
          category: 'conflict',
          message:
            'This older capture needs explicit recovery. Its photo is retained; old AI work will not be replayed.',
        },
      });
      return;
    }
    rapidCaptureAuthority(record);
    if (!shouldContinue()) return;
    // AI admission has its own worker slot after publication. Upload work never
    // waits for capability, credit consent, or provider processing.
    if (
      initial.itemCreated &&
      !hasCaptureEdits(record) &&
      record.analyzeImage &&
      !record.aiStarted &&
      !record.aiDismissed &&
      !record.controlAction
    ) {
      phase = 'ai';
      let acceptedAiCostVersion = record.acceptedAiCostVersion;
      if (!acceptedAiCostVersion) {
        try {
          acceptedAiCostVersion = await prepareConsent?.(record);
        } catch {
          /* An explicit warning keeps this saved item usable. */
        }
      }
      if (!shouldContinue()) return;
      if (!acceptedAiCostVersion) {
        await update({
          state: 'failed',
          aiSkipReason: 'cost-unconfirmed',
          lastError: {
            phase: 'ai',
            category: 'conflict',
            message:
              'The item was saved. AI cost could not be confirmed; retry keyword generation or dismiss this notice.',
          },
        });
        return;
      }
      const prepared = await rapidCaptureDb.update(record, (current) =>
        current.aiDismissed || current.aiStarted || current.controlAction
          ? current
          : {
              ...current,
              acceptedAiCostVersion,
              controlAction: 'analyze',
              aiSkipReason: undefined,
            },
      );
      if (
        !prepared ||
        prepared.aiDismissed ||
        (prepared.aiStarted && !prepared.controlAction)
      )
        return;
      record = prepared;
    }
    if (record.controlAction) {
      const action = record.controlAction;
      if (
        !(await update({ controlAttempts: (record.controlAttempts ?? 0) + 1 }))
      )
        return;
      const receipt = await captureService.control(record, action);
      if (!shouldContinue()) return;
      await applyCaptureStatus(record, receipt, action);
      record = (await rapidCaptureDb.get(record.operationId)) ?? record;
    }
    if (!record.itemCreated) {
      phase = record.mutation ? 'edits' : 'upload';
      if (!record.attempts)
        recordRapidCaptureMetric(
          'queue-wait',
          Math.max(0, Date.now() - Date.parse(record.createdAt)),
        );
      if (
        !(await update({
          state: 'submitting',
          itemCreateAttempted: true,
          attempts: record.attempts + 1,
          ...(record.mutation
            ? { editAttempts: (record.editAttempts ?? 0) + 1 }
            : {}),
          lastError: undefined,
        }))
      )
        return;
      const receipt = await transferMutation(record, update, shouldContinue);
      if (!receipt || !shouldContinue()) return;
      await applyCaptureStatus(record, receipt);
      record = (await rapidCaptureDb.get(record.operationId)) ?? record;
    }
    if (!shouldContinue() || !record.itemCreated || !hasCaptureEdits(record))
      return;
    phase = 'edits';
    if (!record.editSnapshot) {
      if (!Number.isSafeInteger(record.itemRevision))
        throw new Error(
          'The saved item revision is unavailable. Reconnect before syncing edits.',
        );
      const editSnapshot = {
        operationId: createOperationId(),
        localRevision: record.localRevision ?? 0,
        expectedRevision: record.itemRevision!,
        fields: { ...record.edits },
        mergeKeywords: record.mergeKeywords !== false,
        ...(record.pendingImage === undefined
          ? {}
          : { image: record.pendingImage }),
      };
      const pending = await rapidCaptureDb.update(record, (current) =>
        current.editSnapshot ? current : { ...current, editSnapshot },
      );
      if (!pending) return;
      record = pending;
    }
    const snapshot = record.editSnapshot!;
    if (!(await update({ editAttempts: (record.editAttempts ?? 0) + 1 })))
      return;
    const editStarted = performance.now();
    const mutation = editMutation(record, snapshot);
    const receipt = await transferMutation(
      mutation,
      async (changes) => {
        const updated = await rapidCaptureDb.update(record, (current) =>
          current.editSnapshot?.operationId !== snapshot.operationId
            ? current
            : {
                ...current,
                editSnapshot: { ...current.editSnapshot, ...changes },
              },
        );
        return updated?.editSnapshot?.operationId === snapshot.operationId
          ? editMutation(updated, updated.editSnapshot)
          : undefined;
      },
      shouldContinue,
    );
    if (
      !receipt ||
      !receipt.itemCreated ||
      !receipt.savedFields ||
      receipt.itemRevision === undefined
    )
      return;
    const saved = { ...receipt.savedFields, revision: receipt.itemRevision };

    recordRapidCaptureMetric('edit-sync', performance.now() - editStarted);
    if (!shouldContinue()) return;
    await rapidCaptureDb.update(record, (current) => {
      if (current.editSnapshot?.operationId !== snapshot.operationId)
        return current;
      const edits = { ...current.edits };
      const editFieldRevisions = { ...current.editFieldRevisions };
      (Object.keys(snapshot.fields) as Array<keyof CaptureDraftFields>).forEach(
        (key) => {
          if ((editFieldRevisions[key] ?? 0) <= snapshot.localRevision) {
            delete edits[key];
            delete editFieldRevisions[key];
          }
        },
      );
      const imageAcknowledged =
        snapshot.image !== undefined &&
        (current.pendingImageRevision ?? 0) <= snapshot.localRevision;
      const stillDirty =
        Object.keys(edits).length > 0 ||
        (current.pendingImage !== undefined && !imageAcknowledged);
      return {
        ...current,
        edits,
        editFieldRevisions,
        editSnapshot: undefined,
        ...(imageAcknowledged
          ? { pendingImage: undefined, pendingImageRevision: undefined }
          : {}),
        ...(snapshot.image === undefined
          ? {}
          : {
              publishedImage: snapshot.image,
              publishedImageOperationId: snapshot.operationId,
              publishedImageRevision: snapshot.localRevision,
            }),
        publishedStoragePath: receipt.storagePath,
        editAttempts: 0,
        syncedRevision: Math.max(
          current.syncedRevision ?? 0,
          snapshot.localRevision,
        ),
        itemRevision: Math.max(current.itemRevision ?? 0, saved.revision),
        publishedRevision: saved.revision,
        publishedFields: {
          keywords: saved.keywords,
          location: saved.location,
          locationId: saved.locationId,
          qty: saved.qty,
          tagIds: saved.tagIds,
        },
        mergeKeywords:
          stillDirty ||
          saved.revision < (current.itemRevision ?? 0) ||
          (current.analyzeImage &&
            !current.aiDismissed &&
            current.completion !== 'analysis-complete') ||
          !current.completion ||
          Object.values(current.editorLeases ?? {}).some(
            (expiry) => expiry > Date.now(),
          )
            ? current.mergeKeywords
            : false,
        lastError:
          current.lastError?.phase === 'edits' ? undefined : current.lastError,
        nextAttemptAt: undefined,
      };
    });
    invalidateInventory({
      userId: record.userId,
      collectionId: record.collectionId,
      resources: ['items'],
      reloadItemIds: [record.itemId],
    });
  } catch (error) {
    if (!shouldContinue()) return;
    const details = { ...errorDetails(error), phase };
    if (uploadMissing(error)) await update({ uploaded: false });
    const contractCode = (error as { details?: { code?: unknown } })?.details
      ?.code;
    if (
      details.category === 'permission' ||
      ['role-required', 'membership-required', 'collection-not-found'].includes(
        String(contractCode),
      )
    ) {
      await rapidCaptureDb.update(record, (current) =>
        sanitizeRapidCaptureTerminalRecord(
          current,
          contractCode === 'role-required' ? 'membership-changed' : 'revoked',
          details,
        ),
      );
      return;
    }
    const attempts =
      phase === 'edits'
        ? (record.editAttempts ?? 0)
        : phase === 'ai'
          ? (record.controlAttempts ?? 0)
          : record.attempts;
    const retryable =
      ['network', 'unknown', 'quota'].includes(details.category) &&
      attempts < 5;
    await update({
      state: retryable
        ? record.itemCreated
          ? record.completion
            ? 'confirming'
            : 'analysis-submitting'
          : 'pending'
        : 'failed',
      lastError: details,
      ...(phase === 'ai' && contractCode === 'confirmation-required'
        ? { aiSkipReason: 'cost-changed' as const }
        : {}),
      nextAttemptAt: retryable
        ? new Date(
            Date.now() +
              Math.max(
                Math.min(5000 * 2 ** Math.max(0, attempts - 1), 300_000),
                (details.retryAfterSeconds ?? 0) * 1000,
              ),
          ).toISOString()
        : undefined,
    });
  }
};
